import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import notificationManager from '../../utils/notificationManager';

export default function SettingsPage({ user }) {
  const [fontSize, setFontSize] = useState(user.settings?.fontSize || 'medium');
  const [notifications, setNotifications] = useState(user.settings?.notifications !== false);
  const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    // Check current push notification permission
    try {
      if (notificationManager.isSupported()) {
        setPushNotificationsEnabled(notificationManager.getPermission() === 'granted');
      }
    } catch (error) {
      console.error('檢查推送通知權限失敗:', error);
    }
  }, []);

  const handleTogglePushNotifications = async () => {
    try {
      if (!notificationManager.isSupported()) {
        toast.error('您的瀏覽器不支持推送通知');
        return;
      }

      if (pushNotificationsEnabled) {
        // Can't revoke permission programmatically, just inform user
        toast.info('請在瀏覽器設定中關閉推送通知權限');
        return;
      }

      // Show informative message before requesting permission
      toast.info('即將請求通知權限，請在瀏覽器彈窗中選擇「允許」');
      
      // Wait a bit so user can see the message
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Request permission
      const granted = await notificationManager.requestPermission();
      if (granted) {
        setPushNotificationsEnabled(true);
        toast.success('推送通知已啟用');
        
        // Test notification
        setTimeout(() => {
          notificationManager.showNotification('測試通知', {
            body: '推送通知已成功啟用！您現在會收到即時提醒。'
          });
        }, 500);
      } else {
        toast.error('推送通知權限被拒絕，您可以在瀏覽器設定中重新開啟');
      }
    } catch (error) {
      console.error('切換推送通知失敗:', error);
      toast.error('操作失敗，請稍後再試');
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/users/${user.user_id}`, {
        settings: { fontSize, notifications }
      });
      toast.success('設定已儲存');
    } catch (error) {
      toast.error('儲存失敗');
    }
  };

  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error('請填寫所有密碼欄位');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('新密碼和確認密碼不一致');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('密碼長度至少6個字元');
      return;
    }

    try {
      await axios.post(`${API}/users/${user.user_id}/change-password`, {
        old_password: oldPassword,
        new_password: newPassword
      });
      toast.success('密碼已更新');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || '密碼更新失敗');
    }
  };

  return (
    <div className="p-4 md:p-8 pt-20 md:pt-8" data-testid="settings-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-800 mb-8">設定</h1>

        <div className="space-y-6">
          {/* General Settings */}
          <div className="bg-white rounded-2xl shadow-lg p-8 space-y-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">一般設定</h2>
            
            <div>
              <label className="text-lg font-semibold text-gray-800 mb-3 block">字體大小</label>
              <Select value={fontSize} onValueChange={setFontSize}>
                <SelectTrigger data-testid="font-size-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">小</SelectItem>
                  <SelectItem value="medium">中</SelectItem>
                  <SelectItem value="large">大</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="text-lg font-semibold text-gray-800">通知</div>
                <div className="text-sm text-gray-600">接收系統通知和提醒</div>
              </div>
              <Switch 
                data-testid="notifications-switch"
                checked={notifications}
                onCheckedChange={setNotifications}
              />
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <div>
                <div className="text-lg font-semibold text-gray-800">推送通知</div>
                <div className="text-sm text-gray-600">
                  {notificationManager.isSupported() 
                    ? '在手機螢幕上顯示彈出通知' 
                    : '您的瀏覽器不支持推送通知'}
                </div>
                {!pushNotificationsEnabled && notificationManager.isSupported() && (
                  <div className="text-xs text-blue-600 mt-1">
                    💡 點擊啟用後，瀏覽器會彈出權限請求，請選擇「允許」
                  </div>
                )}
              </div>
              <Button
                onClick={handleTogglePushNotifications}
                variant={pushNotificationsEnabled ? "default" : "outline"}
                size="sm"
                disabled={!notificationManager.isSupported()}
              >
                {pushNotificationsEnabled ? '已啟用' : '啟用'}
              </Button>
            </div>

            <div className="pt-6 border-t">
              <Button 
                data-testid="save-settings-btn"
                onClick={handleSave}
                className="w-full bg-orange-500 hover:bg-orange-600"
              >
                儲存設定
              </Button>
            </div>
          </div>

          {/* Change Password */}
          <div className="bg-white rounded-2xl shadow-lg p-8 space-y-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">修改密碼</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">舊密碼</label>
              <Input
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                placeholder="輸入目前密碼"
                data-testid="old-password-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">新密碼</label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="輸入新密碼（至少6個字元）"
                data-testid="new-password-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">確認新密碼</label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="再次輸入新密碼"
                data-testid="confirm-password-input"
              />
            </div>

            <div className="pt-6 border-t">
              <Button 
                data-testid="change-password-btn"
                onClick={handleChangePassword}
                className="w-full bg-blue-500 hover:bg-blue-600"
              >
                更新密碼
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
